/**
 * Main app layout shell
 * Contains navigation and routes with authentication guards
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Authenticated, Unauthenticated, AuthLoading } from 'convex/react';
import { useQuery, useMutation } from 'convex/react';
import { useEffect, useState } from 'react';
import { api } from "convex/_generated/api";
import { SideNav } from './SideNav';
import { TopNav } from './TopNav';
import { DashboardPage } from '../routes/dashboard/DashboardPage';
import { TemplatesPage } from '../routes/templates/TemplatesPage';
import { ProofPage } from '../routes/proof/ProofPage';
import { QALibraryPage } from '../routes/qa-library/QALibraryPage';
import { CallsPage } from '../routes/calls/CallsPage';
import { HistoryPage } from '../routes/history/HistoryPage';
import { AnalyticsPage } from '../routes/analytics/AnalyticsPage';
import { TeamPage } from '../routes/team/TeamPage';
import { BillingPage } from '../routes/billing/BillingPage';
import { SettingsPage } from '../routes/settings/SettingsPage';
import { ProfilesPage } from '../routes/profiles/ProfilesPage';
import { SignInPage } from '../routes/auth/SignInPage';
import { ExtensionAuthPage } from '../routes/auth/ExtensionAuthPage';

function LoadingScreen() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground">Loading...</p>
            </div>
        </div>
    );
}

function AuthenticatedApp() {
    // Ensure user has a team on first sign-in
    const ensureTeam = useMutation(api.onboarding.ensureTeam);
    const currentUser = useQuery(api.onboarding.getCurrentUser);
    const [isOnboarding, setIsOnboarding] = useState(false);

    useEffect(() => {
        // Auto-create team for new users
        if (currentUser !== undefined && currentUser?.team === null && !isOnboarding) {
            setIsOnboarding(true);
            ensureTeam()
                .then(() => {
                    // Team created, the query will refetch automatically
                    setIsOnboarding(false);
                })
                .catch((err) => {
                    console.error('Failed to create team:', err);
                    setIsOnboarding(false);
                });
        }
    }, [currentUser, ensureTeam, isOnboarding]);

    // Show loading while onboarding (creating team for new user)
    if (currentUser === undefined || (currentUser?.team === null)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">
                        {isOnboarding ? 'Setting up your workspace...' : 'Loading...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex">
            {/* Side Navigation */}
            <SideNav />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top Navigation - sticky */}
                <div className="sticky top-0 z-30">
                    <TopNav />
                </div>

                {/* Page Content - scrollable */}
                <main className="flex-1 p-6">
                    <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/templates" element={<TemplatesPage />} />
                        <Route path="/proof" element={<ProofPage />} />
                        <Route path="/qa-library" element={<QALibraryPage />} />
                        <Route path="/calls" element={<CallsPage />} />
                        <Route path="/history" element={<HistoryPage />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/profiles" element={<ProfilesPage />} />
                        <Route path="/team" element={<TeamPage />} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        {/* Extension auth - authenticated users see success and auto-close */}
                        <Route path="/auth/extension" element={<ExtensionAuthPage />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
}

export function AppShell() {
    const location = useLocation();
    const isExtensionAuth = location.pathname === '/auth/extension';

    return (
        <>
            <AuthLoading>
                <LoadingScreen />
            </AuthLoading>

            <Unauthenticated>
                {/* Extension auth has its own sign-in UI */}
                {isExtensionAuth ? <ExtensionAuthPage /> : <SignInPage />}
            </Unauthenticated>

            <Authenticated>
                {/* Extension auth route needs special handling even when authenticated */}
                {isExtensionAuth ? <ExtensionAuthPage /> : <AuthenticatedApp />}
            </Authenticated>
        </>
    );
}
